/**
 * DashCompanion - Página principal de campanhas para motoristas
 * Módulo Motorista - Sistema IDOOH
 * 
 * Funcionalidades:
 * - Visualização de packs de mídias
 * - Lista de mídias em veiculação
 * - Busca por ID de mídia
 * - Gerenciamento de mídias ativas
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Search,
    X,
    LogOut,
    Wifi,
    WifiOff,
    Package,
    DollarSign,
    BarChart3,
    Loader2
} from 'lucide-react';

import { useDashCompanionData } from '../hooks/useDashCompanionData';
import { MediaPackCard } from './MediaPackCard';
import { MediaPackExpanded } from './MediaPackExpanded';
import { ActiveMediaList } from './ActiveMediaList';
import { supabase } from '@/integrations/supabase/client';

export function DashCompanion() {
    const navigate = useNavigate();
    const [isTabletConnected] = useState(true); // Mock - tablet conectado
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);

    const {
        packs,
        activeMedias,
        selectedPack,
        searchQuery,
        isLoading,
        stats,
        setSearchQuery,
        removeMedia,
        activatePack,
        openPack,
        closePack
    } = useDashCompanionData();

    // Verificar status do perfil em tempo real
    useEffect(() => {
        let isMounted = true;

        async function checkProfileStatus() {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                const { data: driver } = await supabase
                    .from('drivers')
                    .select('profile_status')
                    .eq('id', user.id)
                    .single();

                if (!isMounted) return;

                if (driver) {
                    // Redirecionar baseado no status
                    if (driver.profile_status === 'pending_correction') {
                        navigate('/driver/correction', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'rejected') {
                        navigate('/driver/rejected', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'pending_review') {
                        navigate('/driver/review', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'pending_submission') {
                        navigate('/driver/onboarding', { replace: true });
                        return;
                    }
                }

                setIsCheckingStatus(false);
            } catch (error) {
                console.error('[DashCompanion] Erro ao verificar status:', error);
                setIsCheckingStatus(false);
            }
        }

        checkProfileStatus();

        // Listener em tempo real para mudanças no status
        const channel = supabase.channel('driver-status-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'drivers'
                },
                async (payload) => {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user && payload.new.id === user.id) {
                        const newStatus = payload.new.profile_status;
                        console.log('[DashCompanion] Status alterado para:', newStatus);

                        if (newStatus === 'pending_correction') {
                            toast.info('Você tem uma notificação de correção');
                            navigate('/driver/correction', { replace: true });
                        } else if (newStatus === 'rejected') {
                            navigate('/driver/rejected', { replace: true });
                        }
                    }
                }
            )
            .subscribe();

        return () => {
            isMounted = false;
            supabase.removeChannel(channel);
        };
    }, [navigate]);

    // Logout
    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/driver/login', { replace: true });
    };

    // Ativar pack
    const handleActivatePack = (pack: typeof packs[0]) => {
        const count = activatePack(pack);
        if (count > 0) {
            toast.success(`${count} mídia(s) adicionada(s) à veiculação!`);
        } else {
            toast.info('Todas as mídias deste pack já estão em veiculação');
        }
    };

    // Remover mídia
    const handleRemoveMedia = (mediaId: string) => {
        removeMedia(mediaId);
        toast.success('Mídia removida da veiculação');
    };

    // Limpar busca
    const clearSearch = () => {
        setSearchQuery('');
    };

    if (isLoading || isCheckingStatus) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 pt-4 pb-3 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Campanhas</h1>
                        <p className="text-gray-500 text-sm">Gerencie suas mídias</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Status conexão tablet */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${isTabletConnected
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-red-50 text-red-600 border border-red-200'
                            }`}>
                            {isTabletConnected ? (
                                <>
                                    <Wifi className="w-3.5 h-3.5" />
                                    <span>Tablet OK</span>
                                </>
                            ) : (
                                <>
                                    <WifiOff className="w-3.5 h-3.5" />
                                    <span>Offline</span>
                                </>
                            )}
                        </div>
                        {/* Logout */}
                        <button
                            onClick={handleLogout}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                            title="Sair"
                        >
                            <LogOut className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>
                </div>

                {/* SearchBox */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por ID da mídia..."
                        className="w-full pl-10 pr-10 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                    />
                    {searchQuery && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-emerald-100 rounded-full"
                        >
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 pb-6 space-y-6 pt-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <BarChart3 className="w-3.5 h-3.5" />
                            <span>Ativas</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{stats.totalActive}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <Package className="w-3.5 h-3.5" />
                            <span>Total</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{stats.totalMedias}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            <span>Ganhos</span>
                        </div>
                        <p className="text-lg font-bold text-white">R${stats.totalEarnings.toFixed(0)}</p>
                    </div>
                </div>

                {/* Seção Pack de Mídias */}
                {packs.length > 0 && !searchQuery && (
                    <div className="space-y-3">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 px-1">
                            <Package className="w-5 h-5 text-emerald-500" />
                            Pack Disponível
                        </h2>
                        {packs.map(pack => (
                            <MediaPackCard
                                key={pack.id}
                                pack={pack}
                                onOpen={openPack}
                            />
                        ))}
                    </div>
                )}

                {/* Lista de Mídias Ativas */}
                <ActiveMediaList
                    medias={activeMedias}
                    onRemove={handleRemoveMedia}
                />
            </div>

            {/* Modal Pack Expandido */}
            {selectedPack && (
                <MediaPackExpanded
                    pack={selectedPack}
                    onClose={closePack}
                    onActivate={handleActivatePack}
                />
            )}

            {/* Footer */}
            <div className="px-4 pb-8 pt-4 border-t border-gray-100">
                <div className="text-center">
                    <img
                        src="https://locaninja.com/logotipo-watch.png"
                        alt="Watch Logo"
                        className="h-6 mx-auto mb-2 opacity-70"
                    />
                    <p className="text-xs text-gray-400">
                        © 2024 Watch Technologies Inc.
                    </p>
                    <p className="text-xs text-gray-300 mt-1">
                        Versão 1.0.0
                    </p>
                </div>
            </div>

            {/* Estilo para animação */}
            <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
        </div>
    );
}

export default DashCompanion;
